import { formatCopy } from '../../copy/format.js';
import copy from '../../copy/en/shell.json' with { type: 'json' };
/**
 * Loads `/api/config` before anything else renders. Until it arrives the page
 * is painted with the build-time theme (see main.tsx) so there is no flash.
 */
import { useCallback, useEffect, useState } from "react";
import { FullPageError, FullPageSpinner } from "../components/ui/FullPage.js";
import { loadPortalConfig, type PortalConfig } from "../lib/config.js";
import { App } from "./App.js";
import type { JoinConfirmationAttempt } from "../auth/joinConfirmation.js";

type BootState = { kind: "loading" } | { kind: "ready"; config: PortalConfig } | { kind: "error"; message: string };

export function Boot({ joinConfirmationAttempt }: { joinConfirmationAttempt?: JoinConfirmationAttempt | null }) {
  const [state, setState] = useState<BootState>({ kind: "loading" });

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const config = await loadPortalConfig();
      setState({ kind: "ready", config });
    } catch (e) {
      setState({ kind: "error", message: e instanceof Error ? e.message : "unknown" });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === "loading") return <FullPageSpinner label={copy.Boot.startingThePortal} />;
  if (state.kind === "error") {
    return (
      <FullPageError
        title={copy.Boot.thePortalApiIsNotReachable}
        message={formatCopy(copy.Boot.configurationFailure, { message: state.message })}
        onRetry={() => void load()}
      />
    );
  }
  return <App config={state.config} joinConfirmationAttempt={joinConfirmationAttempt} />;
}
